// @flow
import * as csx from 'csstips';
import { sliderInput as input } from '@src/styles/forms';
import { stylesheet } from 'typestyle';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

import colors from '@src/constants/colors';

import type {
  CaratWeightSearchExtentType,
  PriceSearchExtentType,
  DepthSearchExtentType,
  TableSearchExtentType,
  LengthWidthRatioSearchExtentType
} from '@types/search';
import type { ObjectType } from '@types/misc';

declare var noUiSlider: any;

export type GetOptsType = (
  Value: Array<number>,
  Extent: Array<number>
) => ObjectType;

const symbolTrimmer = /[^\d.]/gi;

const classes = stylesheet({
  container: {
    margin: '0 auto 30px auto',
    width: '280px',
    height: '95px',
    position: 'relative',
    $nest: {
      '@media (min-width: 1015px) and (max-width: 1019px)': {
        '&:nth-child(10), &:nth-child(11), &:nth-child(12)': {
          marginBottom: '15px'
        }
      },
      '@media (min-width: 1020px)': {
        '&:nth-child(7), &:nth-child(8), &:nth-child(9), &:nth-child(10), &:nth-child(11), &:nth-child(12)': {
          marginBottom: '15px'
        }
      }
    }
  },
  inputContainer: {
    ...csx.horizontal
  },
  title: {
    color: '#7f7f7f',
    fontSize: '14px',
    lineHeight: '1.153846154'
  },
  labelContainer: {
    ...csx.horizontal,
    ...csx.center,
    height: '40px'
  },
  labelItem: {
    '-ms-flex-preferred-size': '25%',
    flexBasis: '25%',
    fontSize: '13px',
    lineHeight: '1.153846154',
    textAlign: 'center',
    color: '#8a8a8a',
    transition: 'color 0.2s ease-in-out'
  },
  input: Object.assign(input, { width: '70px' }),
  labelQuestionMark: {
    display: 'inline-block',
    marginLeft: '6px',
    position: 'relative',
    lineHeight: 0,
    verticalAlign: 'baseline',
    cursor: 'pointer',
    height: 13
  }
});

const styles = {
  labelHighlighted: {
    color: colors.lumeraMidBlue,
    fontWeight: '700',
    transition: 'color 0.2s ease-in-out'
  }
};

const eventHandlers = {};

type RefType = {
  current: null | React.ElementRef<'div'>
};

type QuestionMarkRefType = {
  current: null | React.ElementRef<'img'>
};

type PropsType = {
  title: string,
  value: Array<number | string>,
  userValue?: Array<number | string> | void,
  extent?: Array<number | string> | void,
  getOpts: GetOptsType,
  sliderOpts?: ObjectType,
  onSliderUpdate: (Values: Array<number | string>) => void,
  inputs?: boolean,
  steps?: Array<string>,
  inputStep: number,
  inputStepMargin: number,
  formatter: any => string,
  inputWidth: string,
  inputAlign?: string,
  inputType?: string,
  leftInputAlign?: string,
  rightInputAlign?: string,
  sliderMarginTop?: string,
  titleMarginBottom?: string,
  children?: React.Element<any>,
  disableTabIndex?: boolean,
  Popup?: React.Node
};

type StateType = {
  extent:
    | CaratWeightSearchExtentType
    | PriceSearchExtentType
    | DepthSearchExtentType
    | TableSearchExtentType
    | LengthWidthRatioSearchExtentType
    | void,
  lower: number,
  upper: number,
  lowerInputValue: ?string,
  upperInputValue: ?string
};

class GenericSlider extends React.Component<PropsType, StateType> {
  slider: any;
  sliderRef: RefType;
  _updateParentComponent: Function;

  static defaultProps = {
    steps: [],
    value: [-100000, 4000000000],
    inputStep: 1,
    inputStepMargin: 0,
    inputWidth: '70px',
    getOpts: () => {},
    onSliderUpdate: () => {},
    formatter: (T: any) => T
  };

  constructor(props: PropsType) {
    super(props);

    this.sliderRef = React.createRef();

    this.state = {
      extent: props.extent,
      lower: Number(props.value[0]),
      upper: Number(props.value[1]),
      lowerInputValue: undefined,
      upperInputValue: undefined
    };
  }

  shouldComponentUpdate(nextProps: PropsType, nextState: StateType) {
    return !(isEqual(this.props, nextProps) && isEqual(this.state, nextState));
  }

  componentDidUpdate(prevProps: PropsType, prevState: StateType) {
    if (prevState.extent || this.props.extent) {
      this.componentDidUpdateWithExtents(prevProps, prevState);
    } else {
      this.componentDidUpdateWithSteps(prevProps, prevState);
    }
  }

  componentDidUpdateWithSteps = (
    prevProps: PropsType,
    prevState: StateType
  ) => {
    // If sliderOpts hasn't changed and we still continue, we can't change the
    // slider value.
    if (
      !this.props.sliderOpts ||
      isEqual(prevProps.sliderOpts, this.props.sliderOpts)
    )
      return;

    const [lower, upper] = this.props.sliderOpts.start;
    const nextState = { lower, upper };

    this.setState(nextState);
    this.slider.updateOptions(this.props.sliderOpts);
  };

  componentDidUpdateWithExtents = (
    prevProps: PropsType,
    prevState: StateType
  ) => {
    if (!prevState.extent || !this.props.extent) return;

    // Values and extents may be stored in redux as strings
    const nextExtent: Array<number> = this.props.extent.map(Number);
    const nextValue: Array<number> = this.props.value.map(Number);
    // $FlowFixMe
    const prevExtent: Array<number> = prevState.extent.map(Number);
    const prevValue: Array<number> = prevProps.value.map(Number);

    if (isEqual(prevExtent, nextExtent) && isEqual(prevValue, nextValue))
      return;

    const nextState = {
      lower: nextValue[0],
      upper: nextValue[1],
      extent: nextExtent
    };

    if (this.props.userValue) {
      const numericUserValues = this.props.userValue
        .map(Number)
        .sort((a, b) => a - b);

      if (numericUserValues[0] < nextState.lower) {
        nextState.lower = numericUserValues[0];
      }

      if (numericUserValues[1] > nextState.upper) {
        nextState.upper = numericUserValues[1];
      }
    }

    if (!this.props.userValue) {
      nextState.lower = nextExtent[0];
    } else if (nextState.lower < nextExtent[0]) {
      nextState.lower = nextExtent[0];
    } else if (nextState.lower > nextExtent[1] - this.props.inputStepMargin) {
      nextState.lower = nextExtent[1] - this.props.inputStepMargin;
    }

    if (!this.props.userValue) {
      nextState.upper = nextExtent[1];
    } else if (nextState.upper > nextExtent[1]) {
      nextState.upper = nextExtent[1];
    } else if (nextState.upper < nextExtent[0] + this.props.inputStepMargin) {
      nextState.upper = nextExtent[0] + this.props.inputStepMargin;
    }

    const opts = this.props.getOpts(nextValue, nextExtent);
    this.slider.updateOptions(opts);

    // $FlowFixMe
    this.setState(nextState);
  };

  componentDidMount() {
    const { value, extent, getOpts } = this.props;
    const sliderEl = this.sliderRef.current;

    const numericValue: Array<number> = value.map(Number);
    const numericExtent: Array<number> | void = extent && extent.map(Number);

    const sliderOpts =
      this.props.sliderOpts ||
      (numericExtent && getOpts(numericValue, numericExtent));
    this.slider = noUiSlider.create(sliderEl, sliderOpts);
    this.slider.on('slide', (vals, handle) => {
      this.setState(
        {
          lower: Number(vals[0]),
          upper: Number(vals[1])
        },
        this._updateParentComponent
      );
    });

    // This makes sure that the state matches the incoming "start" options,
    // because otherwise the wrong labels may be highlighted
    if (this.props.steps && this.props.steps.length > 0) {
      // slider
      if (sliderOpts && sliderOpts.start) {
        const [lower, upper] = sliderOpts.start;
        this.setState({
          lower: Number(lower),
          upper: Number(upper)
        });
      }
    } else {
      // slider is a range with extents
      if (sliderOpts && sliderOpts.start && numericExtent) {
        const [lower, upper] = sliderOpts.start;
        const [lowerExtent, upperExtent] = numericExtent;
        this.setState({
          lower: Math.max(Number(lower), Number(lowerExtent)),
          upper: Math.min(Number(upper), Number(upperExtent))
        });
      }
    }
  }

  _updateParentComponent = debounce(() => {
    if (!this.props.steps) return;

    // Get the "values" based on the array bounds currently selected
    const stringStepValues: Array<string> = this.props.steps.slice(
      this.state.lower,
      this.state.upper
    );
    const numericLowHigh: Array<number> = [this.state.lower, this.state.upper];

    // Alert the parent component of the new "selected" values (either an array of strings or an array with two numeric bounds--lower and upper)
    // $FlowFixMe
    this.props.onSliderUpdate(
      this.props.inputs ? numericLowHigh : stringStepValues
    );
  }, 900);

  _updateBound(value: number | string, bound: string) {
    let trimmedValue =
      typeof value !== 'number'
        ? Number(value.replace(symbolTrimmer, ''))
        : value;

    if (bound === 'lower') {
      if (Number.isNaN(trimmedValue)) trimmedValue = 0;

      if (this.state.extent) {
        trimmedValue = Math.min(
          Math.max(trimmedValue, Number(this.state.extent[0])),
          this.state.upper - this.props.inputStepMargin
        );
      }
    } else if (bound === 'upper') {
      if (Number.isNaN(trimmedValue)) trimmedValue = 999999999999;

      if (this.state.extent) {
        trimmedValue = Math.max(
          Math.min(trimmedValue, Number(this.state.extent[1])),
          this.state.lower + this.props.inputStepMargin
        );
      }
    }

    this.slider.set(
      bound === 'upper' ? [null, trimmedValue] : [trimmedValue, null]
    );
    this.setState(
      {
        [bound]: trimmedValue,
        [`${bound}InputValue`]: undefined
      },
      this._updateParentComponent
    );
  }

  _updateLowerBound = (e: SyntheticFocusEvent<HTMLInputElement>) => {
    this._updateBound(e.currentTarget.value, 'lower');
  };

  _updateLowerBoundInputValue = (e: SyntheticInputEvent<HTMLInputElement>) => {
    this.setState({ lowerInputValue: e.currentTarget.value });
  };

  _updateUpperBound = (e: SyntheticFocusEvent<HTMLInputElement>) => {
    this._updateBound(e.currentTarget.value, 'upper');
  };

  _updateUpperBoundInputValue = (e: SyntheticInputEvent<HTMLInputElement>) => {
    this.setState({ upperInputValue: e.currentTarget.value });
  };

  updateSlider = (i: number) => {
    const closerToLower =
      Math.abs(i - this.state.upper + 1) > Math.abs(i - this.state.lower);
    const value = closerToLower ? i : i + 1;
    const bound = closerToLower ? 'lower' : 'upper';
    this._updateBound(value, bound);
  };

  labelItemUnsafeHTML = (itemText: string) => {
    return { __html: itemText.trim().replace(/\s+/, '<br>') };
  };

  labelItemStyles = (itemIndex: number) => {
    const baseStyles =
      itemIndex >= this.state.lower && itemIndex < this.state.upper
        ? styles.labelHighlighted
        : {};

    if (this.props.title === 'Clarity') {
      return { ...baseStyles, fontSize: '11px' };
    } else {
      return { ...baseStyles, fontSize: '13px' };
    }
  };

  onBlurLower = (e: SyntheticFocusEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    const handlerType = `${this.props.title}_lower`;
    const mouseHandlerType = `${handlerType}_mouse`;
    const handler = eventHandlers[handlerType];
    const mouseupHandler = eventHandlers[mouseHandlerType];

    this._updateLowerBound(e);

    if (handler) {
      document.removeEventListener('click', handler);
      document.removeEventListener('touchend', handler);

      eventHandlers[handlerType] = undefined;
    }

    if (mouseupHandler) {
      target.removeEventListener('mouseup', mouseupHandler);

      eventHandlers[mouseHandlerType] = undefined;
    }
  };

  onBlurUpper = (e: SyntheticFocusEvent<HTMLInputElement>) => {
    const target = e.currentTarget;
    const handlerType = `${this.props.title}_upper`;
    const mouseHandlerType = `${handlerType}_mouse`;
    const handler = eventHandlers[handlerType];
    const mouseupHandler = eventHandlers[mouseHandlerType];

    this._updateUpperBound(e);

    if (handler) {
      document.removeEventListener('click', handler);
      document.removeEventListener('touchend', handler);

      eventHandlers[handlerType] = undefined;
    }

    if (mouseupHandler) {
      target.removeEventListener('mouseup', mouseupHandler);

      eventHandlers[mouseHandlerType] = undefined;
    }
  };

  onFocusInput = (e: SyntheticFocusEvent<HTMLInputElement>, bound: string) => {
    const target = e.currentTarget;
    const handlerType = `${this.props.title}_${bound}`;
    const mouseUpEvent = `${handlerType}_mouseup`;

    target.setSelectionRange(0, 9999);

    eventHandlers[mouseUpEvent] = function(event) {
      target.removeEventListener('mouseup', eventHandlers[mouseUpEvent]);

      event.preventDefault();

      eventHandlers[mouseUpEvent] = undefined;
    };

    target.addEventListener('click', eventHandlers[mouseUpEvent]);

    if (Rails) {
      const eventHandler = (event: MouseEvent | TouchEvent) =>
        this.handleDocumentClick(event, target);

      eventHandlers[handlerType] = eventHandler;

      document.addEventListener('click', eventHandler);
      document.addEventListener('touchend', eventHandler);
    }
  };

  handleDocumentClick = (e: Event, activeEl: HTMLInputElement) => {
    if (activeEl && e.target !== activeEl) {
      activeEl.blur();
    }
  };

  render() {
    const { lower, upper } = this.state;
    const { value, Popup } = this.props;

    if (!this.props.inputs && !this.props.steps) return;

    return (
      <div className={classes.container}>
        {this.props.title && (
          <h3
            className={classes.title}
            style={{ marginBottom: this.props.titleMarginBottom || '12px' }}
          >
            {this.props.title}
            {Popup ? <Popup /> : null}
          </h3>
        )}
        {this.props.inputs ? (
          <div className={classes.inputContainer}>
            <input
              tabIndex={this.props.disableTabIndex ? '-1' : '0'}
              step={this.props.inputStep}
              className={classes.input}
              onBlur={this.onBlurLower}
              onFocus={e => this.onFocusInput(e, 'lower')}
              onChange={this._updateLowerBoundInputValue}
              type={this.props.inputType || 'tel'}
              value={
                typeof this.state.lowerInputValue === 'undefined'
                  ? this.props.formatter(lower || value[0]) || ''
                  : this.state.lowerInputValue
              }
              style={{
                textAlign:
                  this.props.leftInputAlign ||
                  this.props.inputAlign ||
                  'center',
                color: '#7f7f7f',
                width: this.props.inputWidth
              }}
            />
            <input
              tabIndex={this.props.disableTabIndex ? '-1' : '0'}
              step={this.props.inputStep}
              className={classes.input}
              onBlur={this.onBlurUpper}
              onFocus={e => this.onFocusInput(e, 'upper')}
              onChange={this._updateUpperBoundInputValue}
              type={this.props.inputType || 'tel'}
              value={
                typeof this.state.upperInputValue === 'undefined'
                  ? this.props.formatter(upper || value[1]) || ''
                  : this.state.upperInputValue
              }
              style={{
                textAlign:
                  this.props.rightInputAlign ||
                  this.props.inputAlign ||
                  'center',
                color: '#7f7f7f',
                width: this.props.inputWidth,
                marginLeft: 'auto'
              }}
            />
          </div>
        ) : (
          <div
            className={classes.labelContainer}
            style={this.props.steps ? { marginLeft: 9, marginRight: 9 } : {}}
          >
            {this.props.steps &&
              this.props.steps.map((s, i) => {
                return (
                  <div
                    key={i}
                    className={classes.labelItem}
                    style={this.labelItemStyles(i)}
                    dangerouslySetInnerHTML={this.labelItemUnsafeHTML(s)}
                  />
                );
              })}
          </div>
        )}
        <div
          style={{ marginTop: this.props.sliderMarginTop || '14px' }}
          className='noUIslider'
          ref={this.sliderRef}
        />
      </div>
    );
  }
}

export default GenericSlider;
